import { ReactNode } from 'react';
import { ProductLockProps } from '../../../../../../hooks/useProductLock.types';
import { SelloutTotalsProductColor } from '../../../../../../services/modules/product-color-sellout/product-color-sellout.types';
import { getSelloutSummaryFromQuantity } from '../../../../../../services/modules/product-color-sellout/productColorSellout.utils';
import useOrderBookStore from '../../../../../../stores/OrderBook/orderBook.store';
import { CopiedGrid } from '../../../../../orders/orderState.types';
import { UseProductGridOutput } from '../../../../../orders/products/interfaces/use-product-grid.interface';
import {
  OrderProductGridItem,
  UpdateItemFormat,
} from '../../../../../orders/products/orderProducts.types';
import {
  OrderProductForGridContainer,
  OrderProductGridContainerProps,
} from '../../../../../orders/products/product/grid/components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import {
  HandleSelectPacks,
  SelectPacksSideEffect,
} from '../../../../../orders/products/product/grid/components/orderProductGrid/types/commonGrid.types';
import { GridStoreApi } from '../../../../../orders/products/product/grid/store/gridStore';
import { produceItemsToStore } from '../../../../../orders/products/product/grid/store/produceItemsToStore';
import { getTotalPackQuantity } from '../../../../../orders/products/product/grid/utils/packQuantity.utils';
import { CatalogListCombination } from './catalogListCombinations.utils';
import { shouldDisableCatalogListGrid } from './catalogListGridDisable.utils';

interface CatalogListGridCopy {
  copiedGrid: CopiedGrid;
  copyGrid: (grid: CopiedGrid) => void;
  removeCopiedGrid: () => void;
  onClickPaste: () => Promise<void>;
}

interface CatalogListGridOrder {
  orderId?: string;
  selectedOrderId?: string;
}

interface CatalogListGridSurface {
  isPublic: boolean;
  isStacked: boolean;
  canEditOrders: boolean;
  canAddToOrder: boolean;
}

export interface OnCatalogListPacksSelectedParams {
  totalQuantity: number;
}

export interface ToCatalogGridContainerPropsParams {
  product: OrderProductForGridContainer;
  combination: CatalogListCombination;
  productGrid: UseProductGridOutput;
  handleSelectPacks?: HandleSelectPacks;
  grid: OrderProductGridItem[];
  lock: ProductLockProps;
  order: CatalogListGridOrder;
  surface: CatalogListGridSurface;
  gridStoreApi: GridStoreApi;
  copy: CatalogListGridCopy;
  selloutMetrics?: SelloutTotalsProductColor;
  addButton?: ReactNode;
  onNoSelectedOrder: () => void;
  onPacksSelected: (params: OnCatalogListPacksSelectedParams) => void;
}

export type CatalogListGridContainerProps = OrderProductGridContainerProps & {
  ExtraButton?: ReactNode;
};

export const toCatalogGridContainerProps = ({
  product,
  combination,
  productGrid,
  grid,
  handleSelectPacks,
  lock,
  order,
  surface,
  gridStoreApi,
  copy,
  selloutMetrics,
  addButton,
  onNoSelectedOrder,
  onPacksSelected,
}: ToCatalogGridContainerPropsParams): CatalogListGridContainerProps => {
  const allowChangeImmediateGrid = product.productAllowChangeImmediateGrid;
  const allowChangePreOrderGrid = product.productAllowChangePreOrderGrid;

  const stackOrderItem = (item: UpdateItemFormat) => {
    const { orderId, selectedOrderId } = order;

    if (!orderId || !selectedOrderId) {
      onNoSelectedOrder();
      return;
    }

    produceItemsToStore({
      api: gridStoreApi,
      combinationKey: combination.combinationKey,
      items: [{ ...item, orderId }],
    });
  };

  const selectPacksSideEffect: SelectPacksSideEffect = (packs) => {
    const totalQuantity = packs.reduce(
      (total, pack) => total + getTotalPackQuantity(pack.quantitiesBySize, pack.multiplier),
      0,
    );

    onPacksSelected({ totalQuantity });

    useOrderBookStore
      .getState()
      .reloadOrderItemsQuantity(true, order.orderId, order.orderId === order.selectedOrderId);
  };

  return {
    grid,
    product,
    disableAll: shouldDisableCatalogListGrid({
      isPublic: surface.isPublic,
      canEditOrders: surface.canEditOrders,
      canAddToOrder: surface.canAddToOrder,
      hasOrderItem: grid.some((gridItem) => !!gridItem.orderItemId),
      hasKitOrderItem: grid.some((gridItem) => !!gridItem.orderItemKitId),
      hasSelectedPacks: productGrid.selectedPacks.length > 0,
      availability: combination.availability,
      allowChangeImmediateGrid,
      allowChangePreOrderGrid,
    }),
    hideButtons: surface.isPublic,
    readMode: surface.isPublic,
    orderId: order.orderId,
    orderStatus: undefined,
    productSizeNames: grid.map((gridItem) => gridItem.productSizeName),
    getLimit: lock.getLimit,
    getStockValue: lock.getStockValue,
    selectedPacks: productGrid.selectedPacks,
    suggestedPacks: productGrid.suggestedPacks,
    hasValidPacks: productGrid.hasValidPacks,
    resetSelectedPacks: productGrid.resetSelectedPacks,
    selloutSummary: getSelloutSummaryFromQuantity({
      isFetching: false,
      quantitySold: selloutMetrics?.totalQuantitySold,
    }),
    suggestedGrid: [],
    showSuggestedGrid: false,
    suggestionAchieved: false,
    handleSelectPacks,
    selectPacksSideEffect,
    stackOrderItem,
    copiedGrid: copy.copiedGrid,
    copyGrid: copy.copyGrid,
    removeCopiedGrid: copy.removeCopiedGrid,
    handleClickPaste: copy.onClickPaste,
    ExtraButton: surface.isStacked ? addButton : undefined,
  };
};
